import { useRef, useState } from "react";
import { Upload, Star, X, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAuth } from "@/context/AuthContext";
import UploadProgress from "@/components/UploadProgress";
import api, { fmtErr } from "@/lib/api";

const src = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

function SortablePhoto({ url, i, cover, onMakeCover, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: url });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 20 : undefined }}
      className={`group relative bg-background aspect-[3/4] overflow-hidden ${isDragging ? "ring-1 ring-accent" : ""}`}
      data-testid={`portfolio-photo-${i}`}
    >
      <img src={src(url)} alt={`Portfolio ${i + 1}`} className="w-full h-full object-cover" draggable={false} />
      {cover === url && (
        <span className="absolute top-2 left-2 bg-accent text-accent-foreground font-grotesk text-[9px] uppercase tracking-[0.15em] px-2 py-1" data-testid={`cover-badge-${i}`}>
          Cover
        </span>
      )}
      <button
        data-testid={`drag-handle-${i}`}
        aria-label={`Drag photo ${i + 1} to reorder`}
        className="absolute top-2 right-2 bg-black/60 border border-white/30 text-white/80 hover:text-white hover:border-white p-1.5 cursor-grab active:cursor-grabbing touch-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={13} />
      </button>
      <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2 pointer-events-none">
        {cover !== url && (
          <button
            data-testid={`set-cover-${i}`}
            onClick={() => onMakeCover(url)}
            className="pointer-events-auto font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/40 hover:bg-primary hover:text-primary-foreground px-3 py-2 transition-colors inline-flex items-center gap-1.5"
          >
            <Star size={12} /> Cover
          </button>
        )}
        <button
          data-testid={`remove-photo-${i}`}
          onClick={() => onRemove(url)}
          className="pointer-events-auto font-grotesk text-[10px] uppercase tracking-[0.15em] border border-white/40 hover:bg-accent hover:border-accent px-3 py-2 transition-colors inline-flex items-center gap-1.5"
        >
          <X size={12} /> Remove
        </button>
      </div>
    </div>
  );
}

export default function PortfolioManager() {
  const { user, setUser } = useAuth();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // {pct, label} while uploading
  const profile = user?.profile || {};
  const photos = profile.photos || [];
  const cover = profile.photo_url;

  const syncProfile = (patch) => setUser({ ...user, profile: { ...profile, ...patch } });

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      let last;
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData();
        fd.append("file", files[i]);
        const { data } = await api.post("/profile/photos", fd, {
          onUploadProgress: (ev) => setProgress({
            pct: Math.round((ev.loaded / (ev.total || ev.loaded)) * 100),
            label: files.length > 1 ? `Photo ${i + 1} of ${files.length}` : "Uploading photo",
          }),
        });
        last = data;
      }
      if (last) syncProfile({ photos: last.photos, photo_url: last.photo_url });
      toast.success(files.length > 1 ? `${files.length} photos added to your book.` : "Photo added to your book.");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
      setProgress(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (url) => {
    try {
      const { data } = await api.delete("/profile/photos", { data: { url } });
      syncProfile({ photos: data.photos, photo_url: data.photo_url });
      toast.success("Photo removed.");
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const makeCover = async (url) => {
    try {
      const { data } = await api.put("/profile/cover", { url });
      syncProfile({ photo_url: data.photo_url });
      toast.success("Cover updated.");
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldOrder = photos;
    const newOrder = arrayMove(photos, photos.indexOf(active.id), photos.indexOf(over.id));
    syncProfile({ photos: newOrder });  // optimistic
    try {
      await api.put("/profile/photos/reorder", { photos: newOrder });
      toast.success("Order saved — your book reads exactly how you want it.");
    } catch (err) {
      syncProfile({ photos: oldOrder });
      toast.error(fmtErr(err));
    }
  };

  return (
    <div className="panel p-8" data-testid="portfolio-manager">
      <div className="flex items-center justify-between mb-8">
        <p className="overline-label !font-semibold !text-white/85">The portfolio book ({photos.length})</p>
        <button
          data-testid="portfolio-upload-button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="btn-outline !px-5 !py-2.5 disabled:opacity-40"
        >
          <Upload size={14} /> {busy ? "Uploading…" : "Add photos"}
        </button>
        <input
          ref={inputRef}
          data-testid="portfolio-file-input"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
          multiple
          className="hidden"
          onChange={upload}
        />
      </div>
      {busy && progress && <UploadProgress percent={progress.pct} note={progress.label} />}

      {photos.length === 0 ? (
        <p className="font-display italic text-2xl text-white/40" data-testid="portfolio-empty">
          An empty book is an unanswered call — add your first frames.
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={photos} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10">
              {photos.map((p, i) => (
                <SortablePhoto key={p} url={p} i={i} cover={cover} onMakeCover={makeCover} onRemove={remove} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <p className="mt-6 font-grotesk font-semibold text-[11px] uppercase tracking-[0.15em] text-white/70">
        JPEG · PNG · WebP · iPhone photos — up to 25MB each · drag the handles to reorder
      </p>
    </div>
  );
}
